import { Box, Typography } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { WasteManifestSource } from "generated-graphql/graphql";
import { ReactNode, useState } from "react";

import { FieldProvenancePopover, ProvenanceSourceValue } from "../FieldProvenancePopover";
import { resolveByPriority, SOURCE_LABEL, SOURCE_ORDER } from "../manifestSourceDisplay";
import { SourceLetterBox } from "../SourceLetterBox";
import { SourcesDisagreeTooltip } from "../SourcesDisagreeTooltip";
import { FieldSourceValues } from "../useManifestDiscrepancyFields";

export interface SourcedFieldProps {
  label: string;
  /**
   * The manifest's primary source. The value shown defaults to this source,
   * falling back to the next available source in {@link SOURCE_ORDER} when the
   * primary has no value for the field.
   */
  source: WasteManifestSource;
  availableSources: readonly WasteManifestSource[];
  /** Structural per-source display values pulled from the fragment. */
  values: Partial<Record<WasteManifestSource, string | null | undefined>>;
  /** Per-source values from discrepancy rows; override structural values when present. */
  discrepancyValues?: FieldSourceValues;
  renderValue?: (value: ReactNode) => ReactNode;
  /** When true, sources disagree on this field — shows a warning icon by the label. */
  hasDisagreement?: boolean;
  /** Wires the provenance popover's "Open compare" link to the compare dialog (staff only). */
  onOpenCompare?: () => void;
}

/**
 * A read-only label/value row that shows the manifest's primary-source value,
 * falling back to the next source in the hierarchy when the primary lacks one.
 * When the displayed value comes from a fallback (non-primary) source, a small
 * color-by-source dot sits next to the label — naming the source on hover and
 * opening an all-source provenance popover on click. Primary-source values
 * carry no dot (the section header chip communicates the primary). A
 * disagreement warning icon sits by the label when sources conflict.
 */
export function SourcedField({
  label,
  source,
  availableSources,
  values,
  discrepancyValues,
  renderValue,
  hasDisagreement = false,
  onOpenCompare,
}: SourcedFieldProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const merged = { ...values, ...discrepancyValues };

  // When sources disagree, the warning tooltip lists each source's value.
  const disagreementTooltip = hasDisagreement ? (
    <SourcesDisagreeTooltip values={merged} />
  ) : undefined;

  // Prefer the primary source; otherwise the first available source in the
  // hierarchy that actually has a value (shared resolver — see resolveByPriority).
  const resolved = resolveByPriority(source, availableSources, merged);
  const effectiveSource = resolved.source ?? source;

  if (!resolved.present) {
    return (
      <DisplayOnlyField
        label={label}
        copyable={false}
        warningIcon={hasDisagreement}
        warningTooltip={disagreementTooltip}
        renderValue={() => (
          <Typography color="text.disabled" variant="body2">
            —
          </Typography>
        )}
      />
    );
  }

  // A dot is shown only when the value came from a fallback source — the primary
  // is implied by the section header chip, so primary values stay dot-free.
  const isFallback = effectiveSource !== source;

  const provenanceValues: ProvenanceSourceValue[] = SOURCE_ORDER.filter((s) =>
    availableSources.includes(s)
  ).map((s) => ({ source: s, value: merged[s] }));

  const fallbackDot = isFallback ? (
    <SourceLetterBox
      source={effectiveSource}
      tooltip={`Data source: ${SOURCE_LABEL[effectiveSource]}`}
      onClick={(event) => setAnchorEl(event.currentTarget)}
    />
  ) : undefined;

  // Render the dot inline beside the label (centered), not in the top-right slot.
  const labelNode = fallbackDot ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {label}
      {fallbackDot}
    </Box>
  ) : (
    label
  );

  return (
    <>
      <DisplayOnlyField
        text={resolved.value ?? undefined}
        label={labelNode}
        copyable={false}
        renderValue={renderValue}
        warningIcon={hasDisagreement}
        warningTooltip={disagreementTooltip}
      />
      {fallbackDot && (
        <FieldProvenancePopover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          fieldLabel={label}
          values={provenanceValues}
          activeSource={effectiveSource}
          onOpenCompare={onOpenCompare}
        />
      )}
    </>
  );
}
